<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';

import SectionLayout from '../../account/components/SectionLayout.vue';
import UsageKpiCards from '../components/UsageKpiCards.vue';
import UsageBreakdownChart from '../components/UsageBreakdownChart.vue';
import CrmLineChart from 'dashboard/routes/dashboard/crm/components/reports/CrmLineChart.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  estimateCostFromTotal,
  calculateTotalCost,
  formatCost,
  formatNumber,
} from '../utils/costCalculator';

const props = defineProps({
  alertsConfig: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['configChange']);

const { t } = useI18n();
const captainConfigStore = useCaptainConfigStore();

const usage = computed(() => captainConfigStore.usage);
const currentMonth = computed(() => usage.value?.current_month || {});
const totalTokens = computed(() => currentMonth.value?.total_tokens || 0);
const byFeature = computed(() => currentMonth.value?.by_feature || {});
const byModel = computed(() => currentMonth.value?.by_model || {});

// --- Mock chart data (distribute total across 30 days) ---
const mockChartLabels = computed(() => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const labels = [];
  for (let i = 0; i < daysInMonth; i++) {
    const d = new Date(year, month, i + 1);
    labels.push(
      d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' })
    );
  }
  return labels;
});

const mockChartDatasets = computed(() => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const total = totalTokens.value;

  if (total === 0) return [{ label: 'Tokens', data: [], fill: true, color: '#3b82f6' }];

  const avgPerDay = total / daysInMonth;
  const data = Array.from({ length: daysInMonth }, (_, i) => {
    const day = new Date(year, month, i + 1);
    const isWeekend = [0, 6].includes(day.getDay());
    const factor = isWeekend
      ? 0.3 + Math.random() * 0.4
      : 0.7 + Math.random() * 0.6;
    return Math.round(avgPerDay * factor);
  });

  return [
    {
      label: t('CAPTAIN_SETTINGS.USAGE.CHART.TOKENS_LABEL'),
      data,
      fill: true,
      color: '#3b82f6',
    },
  ];
});

// --- By-model table rows ---
// Endpoint only provides total_tokens per model, not input/output split.
// We estimate using a 30% input / 70% output ratio.
const INPUT_RATIO = 0.3;

const modelRows = computed(() => {
  const entries = Object.entries(byModel.value);
  if (entries.length === 0) return [];
  return entries
    .map(([model, tokens]) => {
      const inputTokens = Math.round(tokens * INPUT_RATIO);
      const outputTokens = tokens - inputTokens;
      const cost = estimateCostFromTotal(model, tokens);
      return { model, tokens, inputTokens, outputTokens, cost };
    })
    .sort((a, b) => b.tokens - a.tokens);
});

const modelTableTotalCost = computed(() => {
  return modelRows.value.reduce((sum, row) => sum + row.cost, 0);
});

const hasModelData = computed(() => modelRows.value.length > 0);

// --- Cost Alert ---
const costAlertEnabled = computed(
  () => props.alertsConfig.cost_alert_enabled === true
);
const costAlertThreshold = computed(
  () => props.alertsConfig.cost_alert_threshold || 0
);

const currentMonthCost = computed(() => {
  return calculateTotalCost(byModel.value);
});

const spendingPercentage = computed(() => {
  if (!costAlertThreshold.value || costAlertThreshold.value <= 0) return 0;
  return (currentMonthCost.value / costAlertThreshold.value) * 100;
});

const progressBarClass = computed(() => {
  const pct = spendingPercentage.value;
  if (pct > 100) return 'bg-n-ruby-9';
  if (pct > 80) return 'bg-n-amber-9';
  return 'bg-n-teal-9';
});

const updateAlertConfig = data => {
  emit('configChange', { section: 'alerts', data });
};

const toggleCostAlert = () => {
  updateAlertConfig({ cost_alert_enabled: !costAlertEnabled.value });
};

const handleThresholdInput = event => {
  const value = parseFloat(event.target.value);
  updateAlertConfig({
    cost_alert_threshold: Number.isNaN(value) ? 0 : value,
  });
};
</script>

<template>
  <div class="flex flex-col gap-1">
    <!-- Description -->
    <div class="pb-2">
      <p class="text-sm text-n-slate-11 mb-0">
        {{ t('CAPTAIN_SETTINGS.USAGE.INTRO') }}
      </p>
    </div>

    <!-- KPI Cards -->
    <SectionLayout
      :title="t('CAPTAIN_SETTINGS.USAGE.OVERVIEW.TITLE')"
      :description="t('CAPTAIN_SETTINGS.USAGE.OVERVIEW.DESCRIPTION')"
    >
      <UsageKpiCards :current-month="currentMonth" />
    </SectionLayout>

    <!-- Chart (mock data) -->
    <SectionLayout
      :title="t('CAPTAIN_SETTINGS.USAGE.CHART.TITLE')"
      :description="t('CAPTAIN_SETTINGS.USAGE.CHART.DESCRIPTION')"
      with-border
    >
      <div v-if="totalTokens > 0">
        <CrmLineChart
          :labels="mockChartLabels"
          :datasets="mockChartDatasets"
          :height="220"
        />
        <p class="text-xs text-n-slate-10 mt-2 mb-0 italic">
          {{ t('CAPTAIN_SETTINGS.USAGE.CHART.MOCK_DISCLAIMER') }}
        </p>
      </div>
      <div
        v-else
        class="flex items-center justify-center py-12 text-sm text-n-slate-11"
      >
        {{ t('CAPTAIN_SETTINGS.USAGE.CHART.NO_DATA') }}
      </div>
    </SectionLayout>

    <!-- Breakdown by Feature -->
    <SectionLayout
      :title="t('CAPTAIN_SETTINGS.USAGE.BREAKDOWN.TITLE')"
      :description="t('CAPTAIN_SETTINGS.USAGE.BREAKDOWN.DESCRIPTION')"
      with-border
    >
      <UsageBreakdownChart :by-feature="byFeature" />
    </SectionLayout>

    <!-- Cost by Model -->
    <SectionLayout
      :title="t('CAPTAIN_SETTINGS.USAGE.BY_MODEL.TITLE')"
      :description="t('CAPTAIN_SETTINGS.USAGE.BY_MODEL.DESCRIPTION')"
      with-border
    >
      <div v-if="hasModelData">
        <div class="overflow-x-auto rounded-lg border border-n-weak">
          <table class="w-full text-sm min-w-[36rem]">
            <thead>
              <tr class="bg-n-alpha-black2">
                <th
                  class="text-left px-4 py-2.5 font-medium text-n-slate-12"
                >
                  {{ t('CAPTAIN_SETTINGS.USAGE.BY_MODEL.MODEL') }}
                </th>
                <th
                  class="text-right px-4 py-2.5 font-medium text-n-slate-12"
                >
                  {{ t('CAPTAIN_SETTINGS.USAGE.BY_MODEL.INPUT_TOKENS') }}
                </th>
                <th
                  class="text-right px-4 py-2.5 font-medium text-n-slate-12"
                >
                  {{ t('CAPTAIN_SETTINGS.USAGE.BY_MODEL.OUTPUT_TOKENS') }}
                </th>
                <th
                  class="text-right px-4 py-2.5 font-medium text-n-slate-12"
                >
                  {{ t('CAPTAIN_SETTINGS.USAGE.BY_MODEL.TOTAL_TOKENS') }}
                </th>
                <th
                  class="text-right px-4 py-2.5 font-medium text-n-slate-12"
                >
                  {{ t('CAPTAIN_SETTINGS.USAGE.BY_MODEL.ESTIMATED_COST') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in modelRows"
                :key="row.model"
                class="border-t border-n-weak"
              >
                <td class="px-4 py-2.5 text-n-slate-12 font-mono text-xs">
                  {{ row.model }}
                </td>
                <td class="px-4 py-2.5 text-right text-n-slate-11">
                  {{ formatNumber(row.inputTokens) }}
                  <span class="text-n-slate-9 text-xs">*</span>
                </td>
                <td class="px-4 py-2.5 text-right text-n-slate-11">
                  {{ formatNumber(row.outputTokens) }}
                  <span class="text-n-slate-9 text-xs">*</span>
                </td>
                <td class="px-4 py-2.5 text-right text-n-slate-11">
                  {{ formatNumber(row.tokens) }}
                </td>
                <td class="px-4 py-2.5 text-right text-n-slate-11">
                  ~{{ formatCost(row.cost) }}
                </td>
              </tr>
              <!-- Total -->
              <tr class="border-t-2 border-n-weak bg-n-alpha-black2">
                <td
                  class="px-4 py-2.5 font-medium text-n-slate-12"
                  colspan="3"
                >
                  {{ t('CAPTAIN_SETTINGS.USAGE.BY_MODEL.TOTAL') }}
                </td>
                <td
                  class="px-4 py-2.5 text-right font-medium text-n-slate-12"
                >
                  {{ formatNumber(totalTokens) }}
                </td>
                <td
                  class="px-4 py-2.5 text-right font-medium text-n-slate-12"
                >
                  ~{{ formatCost(modelTableTotalCost) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="text-xs text-n-slate-10 mt-2 mb-0 italic">
          {{ t('CAPTAIN_SETTINGS.USAGE.BY_MODEL.ESTIMATE_NOTE') }}
        </p>
      </div>
      <div v-else class="text-sm text-n-slate-11 py-4 text-center">
        {{ t('CAPTAIN_SETTINGS.USAGE.BY_MODEL.NO_DATA') }}
      </div>
    </SectionLayout>

    <!-- Cost Alert -->
    <SectionLayout
      :title="t('CAPTAIN_SETTINGS.USAGE.ALERT.TITLE')"
      :description="t('CAPTAIN_SETTINGS.USAGE.ALERT.DESCRIPTION')"
      with-border
    >
      <div class="flex flex-col gap-4 p-4 rounded-xl border border-n-weak bg-n-solid-1">
        <!-- Header row: icon + text + toggle -->
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <span
              class="inline-flex items-center justify-center size-9 rounded-lg"
              :class="
                costAlertEnabled
                  ? 'bg-n-amber-3 text-n-amber-11'
                  : 'bg-n-alpha-black2 text-n-slate-10'
              "
            >
              <Icon icon="i-lucide-bell" class="size-4" />
            </span>
            <div>
              <p class="text-sm font-medium text-n-slate-12 mb-0">
                {{ t('CAPTAIN_SETTINGS.USAGE.ALERT.TOGGLE_LABEL') }}
              </p>
              <p class="text-xs text-n-slate-11 mb-0">
                {{ t('CAPTAIN_SETTINGS.USAGE.ALERT.TOGGLE_DESC') }}
              </p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            :aria-checked="costAlertEnabled"
            class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
            :class="costAlertEnabled ? 'bg-n-brand' : 'bg-n-slate-8'"
            @click="toggleCostAlert"
          >
            <span
              class="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
              :class="costAlertEnabled ? 'translate-x-5' : 'translate-x-0'"
            />
          </button>
        </div>

        <!-- Threshold input (visible when enabled) -->
        <div v-if="costAlertEnabled" class="flex items-center gap-3">
          <div class="relative flex-1 max-w-[200px]">
            <span
              class="absolute inset-y-0 start-0 flex items-center ps-3 text-sm font-mono text-n-slate-10 pointer-events-none select-none"
            >
              US$
            </span>
            <input
              type="number"
              :value="costAlertThreshold || ''"
              min="1"
              step="5"
              :placeholder="t('CAPTAIN_SETTINGS.USAGE.ALERT.PLACEHOLDER')"
              class="block w-full h-10 ps-11 pe-3 py-2 text-sm rounded-lg border-none outline outline-1 outline-offset-[-1px] outline-n-weak bg-n-alpha-black2 text-n-slate-12 hover:outline-n-slate-6 focus:outline-n-blue-9 placeholder:text-n-slate-10"
              @input="handleThresholdInput"
            />
          </div>
          <span class="text-xs text-n-slate-11">
            {{ t('CAPTAIN_SETTINGS.USAGE.ALERT.PER_MONTH') }}
          </span>
        </div>

        <!-- Progress bar (visible when enabled + threshold set) -->
        <div
          v-if="costAlertEnabled && costAlertThreshold > 0"
          class="flex items-center gap-3"
        >
          <div
            class="h-2 flex-1 bg-n-alpha-black2 rounded-full overflow-hidden"
          >
            <div
              class="h-full rounded-full transition-all duration-500"
              :class="progressBarClass"
              :style="{
                width: Math.min(spendingPercentage, 100) + '%',
              }"
            />
          </div>
          <span
            class="text-xs font-mono whitespace-nowrap select-none"
            :class="
              spendingPercentage > 100 ? 'text-n-ruby-11' : 'text-n-slate-11'
            "
          >
            US{{ formatCost(currentMonthCost) }} /
            US{{ formatCost(costAlertThreshold) }}
          </span>
        </div>
      </div>
    </SectionLayout>
  </div>
</template>
